<template>
  <div class="shell-sidebar-wrapper">
    <div class="shell-sidebar-overlay"></div>
    <nav
      id="shell.sidebar"
      :class="['card shell-sidebar', {'shell-sidebar-peek': peek}, {'shell-sidebar-open': open}]"
      :style="{top}"
      @transitionstart="if (open === true) top = '0';"
      @transitionend="if (open === false) top = '';"
    >
      <aside
        v-if="open"
        class="d-flex align-items-center justify-content-between px-3"
        style="height: var(--shell-header-height)"
      >
        <div class="h-100" style="cursor: pointer" @click="$router.push('/')"><LogoEkco /></div>
        <IconButton
          id="shell.sidebar.open"
          icon-name="material-symbols:side-navigation"
          variant="ghost"
          size="lg"
          @click="open = false"
        />
      </aside>
      <TreeView
        :tree="tree"
        :class="['w-100 px-3 pb-3 shell-sidebar-tree', {'pt-3': open === false}]"
        role="group"
        :loading="loading"
        :active="active"
        @update:active="handleActive"
      >
        <template #nodeIcon="slotProps">
          <Icon
            v-if="slotProps.node.meta.icon"
            :class="`icon-${slotProps.node.meta.icon}`"
            :icon="`material-symbols:${slotProps.node.meta.icon}`"
          />
        </template>
        <template v-if="errorMessage" #error>
          <ErrorMessageAlert :error-message="errorMessage" class="me-3" />
        </template>
      </TreeView>
    </nav>
  </div>
</template>
<script setup lang="ts">
import {useMouse} from '@vueuse/core';
import {TreeNav} from '@/stores/apolloPlatform/cores/portalNavigation';
import {TreeViewNode} from '@/components/shared/custom/TreeView/TreeViewNode.vue';
const {tree} = defineProps<{loading: boolean; errorMessage?: string; tree: TreeNav}>();
const emit = defineEmits<{close: []; navigate: [meta: SpherePageMeta<SpherePageType>]}>();

const active = ref<TreeViewNode | undefined>();
const handleActive = (node: TreeViewNode) => {
  active.value = node;
  emit('navigate', node.meta);
};
const route = useRoute();
watch(
  () => route.name,
  () => (active.value = route.meta.node),
);

const open = defineModel<boolean>('open', {required: true});
const peek = defineModel<boolean>('peek', {required: true});

const {x} = useMouse({touch: false});
const sidebarEdge = ref(0);
/*
 * N.B. If we set `top` as well as `transform` during
 * a CSS transition, the sidebar will move diagonally.
 * See @transitionstart & @transitionend in the template.
 */
const top = ref<'0' | ''>('0');
watch(open, (open) => {
  if (open) top.value = '0';
});

onMounted(() => {
  const el = document.getElementById('shell.sidebar')!;
  // Avoid calling this often as it forces a layout calculation
  const {width} = el.getBoundingClientRect();
  sidebarEdge.value = width + 16;
});

watch(x, (x) => {
  if (open.value === false) {
    if (peek.value === false && x < 16) {
      // Mouse enter the left side of your screen to peek!
      peek.value = true;
    } else if (peek.value === true && x > sidebarEdge.value) {
      // N.B. This includes the full screen height to help with clicking open!
      peek.value = false;
    }
  }
});
</script>
<style lang="scss" scoped>
@use '@/scss/breakpoints/breakpoints' as *;

.shell-sidebar-overlay {
  display: none;
  position: fixed;
  inset: 0;
  background-color: var(--bs-dark);
  z-index: 10;
}

.shell-sidebar {
  display: flex;
  flex-direction: column;
  position: fixed;
  width: var(--shell-sidebar-width);
  border-left: 0;
  border-top: 0;
  border-bottom: 0;
  border-radius: 0;
  border-color: var(--bs-border-color);
  top: calc(var(--shell-header-height) + 0.5rem);
  bottom: 0;
  left: 0;
  transition-property: transform, top;
  transition-timing-function: ease;
  transition-duration: 200ms;
  z-index: 11;

  @include media-breakpoint-down(sm, $grid-breakpoints) {
    width: 100%;
    border: 0;
    top: 0;
  }
}

.shell-sidebar-open {
  transform: translate3d(0, 0, 0);
}

.shell-sidebar:not(.shell-sidebar-open) {
  transform: translate3d(-100%, 0, 0);
}

.shell-sidebar-peek {
  @include media-breakpoint-up(sm, $grid-breakpoints) {
    transform: translate3d(0, 0, 0) !important; // Don't peak on mobile!
    left: 0.5rem;
    bottom: 0.5rem;
    border: 0;
    border-radius: var(--bs-border-radius);
    box-shadow: var(--bs-box-shadow-lg);
  }
}

:deep(.logo-ekco) {
  height: 100% !important;
  padding: 0.25rem 0;
}

/* Ben's style overwrites for TreeViewNode.vue */

:deep(.tree-node-active) {
  .tree-node-label,
  .tree-child-node-indent {
    color: var(--bs-primary-border-subtle) !important;
    background-color: var(--bs-primary-bg-subtle) !important;
  }

  .tree-node-label-details,
  .tree-node-icon {
    color: var(--bs-primary) !important;
  }
}

:deep(.shell-sidebar-tree > li > button) {
  .tree-node-label-details {
    padding: 0.5em 0;
  }

  .tree-node-icon,
  .tree-parent-node-toggle {
    margin-block-start: 0.5rem;
  }
}

:deep(.tree-node-icon) {
  margin-right: 0.5rem;
}
</style>
